//#region header */
/**************************************************************************************************
//
//  Description: Form to display the application information
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.02.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { ChangeEvent, Fragment, SyntheticEvent, useEffect, useState } from "react";

import useUserQueryStore from "../context/UserStore";

import { ApplicationNamesDataDto } from "../dtos/applicationNamesDto";

import { Box, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";

import { ExpandMore, ChevronRight } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ClearIcon from "@mui/icons-material/Clear";
import AppsIcon from "@mui/icons-material/Apps";

import {
  ActionIconStyle,
  ClearSearchIconStyle,
  dataFormStyle,
  filterToolbarStyle,
  FormInputStyle,
  menuItemStyle,
  menuStyle,
  toolbarStyle,
  tooltipStyle,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import {
  adsBlueA,
  adsDarkRed,
  adsLightGreyB,
  adsPaleBlueA,
  adsRed,
  adsRed10,
  adsRed20,
} from "../utils/ADSColour";
import ADSActionButton, { ADSActionButtonVariant } from "../components/ADSActionButton";

interface ApplicationTreeProps {
  data: ApplicationNamesDataDto[];
  selectedItem: number | undefined;
  expanded: number[];
  haveErrors: boolean;
  errorIds: number[];
  onItemSelected: (id: number | undefined) => void;
  onAddApplication: () => void;
  onDeleteApplication: (id: number) => void;
  onExpandedItemsChange: (nodeIds: number[]) => void;
}

const ApplicationTree: React.FC<ApplicationTreeProps> = ({
  data,
  selectedItem,
  expanded,
  haveErrors,
  errorIds,
  onItemSelected,
  onAddApplication,
  onDeleteApplication,
  onExpandedItemsChange,
}) => {
  const theme = useTheme();

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const [filterBy, setFilterBy] = useState<string>("");
  const [filteredData, setFilteredData] = useState<ApplicationNamesDataDto[]>(data);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /**
   * Event to handle when the application menu is clicked.
   *
   * @param event The event object.
   */
  const handleApplicationMenuClick = (event: SyntheticEvent<HTMLElement | null>) => {
    setAnchorEl(event.nativeEvent.target as HTMLElement);
  };

  /**
   * Event to handle when the application menu is closed.
   *
   * @param event The event object.
   */
  const handleApplicationMenuClose = (event: SyntheticEvent) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  /**
   * Event to handle when the add application menu item is clicked.
   *
   * @param event The event object.
   */
  const handleAddApplicationClick = (event: SyntheticEvent): void => {
    handleApplicationMenuClose(event);
    if (onAddApplication) onAddApplication();
  };

  /**
   * Event to handle when the filter changes.
   *
   * @param newValue The new value to filter by.
   */
  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    setFilterBy(newValue);
    if (newValue.length > 0) {
      const filteredList: ApplicationNamesDataDto[] = data.filter((app) =>
        (app.name ?? "").toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredData(filteredList);
      const foundCurrent = filteredList.find((app) => app.id === selectedItem);
      if (!foundCurrent && onItemSelected) onItemSelected(undefined);
    } else {
      setFilteredData(data);
    }
  };

  /**
   * Event to handle when the clear search button is clicked.
   */
  const handleClearSearch = (): void => {
    setFilterBy("");
    setFilteredData(data);
  };

  /**
   * Event to handle when an application is selected.
   *
   * @param {object} event The event object.
   * @param {string} itemIds The id of the selected application.
   */
  function handleApplicationSelected(event: SyntheticEvent, itemIds: string | null) {
    event.stopPropagation();
    if (onItemSelected) onItemSelected(Number(itemIds) ?? undefined);
  }

  /**
   * Event to handle the toggling of the applications.
   *
   * @param {object} event The event object.
   * @param {array} applicationNames The list of application names that are expanded.
   */
  function handleApplicationToggle(event: SyntheticEvent, applicationNames: string[]): void {
    event.stopPropagation();
    const iconClicked = (event.target as HTMLElement).closest(".MuiTreeItem-iconContainer");
    if (iconClicked && onExpandedItemsChange) {
      onExpandedItemsChange(applicationNames.map((id) => Number(id)));
    }
  }

  /**
   * Method to handle the event when the user chooses to delete the selected application.
   */
  const onDeleteClick = (id: number) => {
    if (onDeleteApplication) {
      onDeleteApplication(id);
    }
  };

  /**
   * Method to get styling used for a tree item, based on whether usrn is current street and/or checked.
   *
   * @param {number} id The id for the application
   * @returns {object} The styling for the tree item.
   */
  const treeItemStyle = (id: number, hasError: boolean): object => {
    const currentApplication = id === selectedItem;
    if (hasError)
      return {
        backgroundColor: adsRed10,
        color: adsRed,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsRed20,
          color: adsDarkRed,
        },
      };
    else if (currentApplication)
      return {
        backgroundColor: adsPaleBlueA,
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
    else
      return {
        backgroundColor: "inherit",
        borderTop: `solid ${adsLightGreyB} 1px`,
        pb: theme.spacing(1),
        pt: theme.spacing(1),
        "&:hover": {
          backgroundColor: adsPaleBlueA,
          color: adsBlueA,
        },
      };
  };

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-application-tree-toolbar">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: "12px", pr: "12px", pt: "4px" }}
        >
          <Typography variant="body2">{`${
            data.length === filteredData.length
              ? data.length
              : filteredData.length.toString() + " of " + data.length.toString()
          } applications`}</Typography>
          <Stack direction="row" alignItems="center" justifyContent={"flex-end"} spacing={1}>
            <Tooltip title="Actions" arrow placement="right" sx={tooltipStyle}>
              <IconButton
                onClick={handleApplicationMenuClick}
                aria-controls="application_action-menu"
                aria-haspopup="true"
                size="small"
              >
                <MoreVertIcon sx={ActionIconStyle()} />
              </IconButton>
            </Tooltip>
            <Menu
              id="application_action-menu"
              elevation={2}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleApplicationMenuClose}
              sx={menuStyle}
            >
              {currentUser?.isSuperAdministrator && (
                <MenuItem dense onClick={handleAddApplicationClick} sx={menuItemStyle(false)}>
                  <Typography variant="inherit">Add application</Typography>
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Stack>
      </Box>
      <Box sx={filterToolbarStyle} id="ads-application-tree-filter-toolbar">
        <TextField
          id="application-filter"
          label="Filter by application name"
          sx={FormInputStyle(false)}
          variant="outlined"
          fullWidth
          margin="dense"
          size="small"
          value={filterBy}
          onChange={handleFilterChange}
          placeholder="Filter by application name"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton id="btnClear" onClick={handleClearSearch} aria-label="Clear filter" size="small">
                    <ClearIcon sx={ClearSearchIconStyle(filterBy)} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={dataFormStyle("ApplicationTree")}>
        <SimpleTreeView
          aria-label="application tree"
          id="application-tree"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            "& .MuiTreeItem-content": {
              borderRadius: 0,
              margin: 0,
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
            },
            "& .MuiTreeItem-content:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiTreeItem-content.Mui-focused, & .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected:hover":
              {
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
              },
            "& .MuiTreeItem-label": {
              backgroundColor: "transparent !important",
            },
          }}
          slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
          expandedItems={expanded.map((id) => id.toString())}
          selectedItems={selectedItem !== undefined ? selectedItem.toString() : null}
          onSelectedItemsChange={handleApplicationSelected}
          onExpandedItemsChange={handleApplicationToggle}
        >
          {filteredData
            .sort((a, b) =>
              (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" })
            )
            .map((rec, index) => {
              return (
                <TreeItem
                  key={`application-${rec.name}-${index}`}
                  itemId={`${rec.id}`}
                  sx={treeItemStyle(rec.id, haveErrors && errorIds.includes(rec.id))}
                  label={
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1}>
                        <AppsIcon />
                        <Typography variant="subtitle2">{rec.name}</Typography>
                      </Stack>
                      {currentUser?.isSuperAdministrator && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={1}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                        >
                          <ADSActionButton
                            id={`delete-connection-${rec.id}`}
                            variant={ADSActionButtonVariant.delete}
                            inheritBackground
                            tooltipTitle={
                              data.length <= 1
                                ? "Cannot delete the last remaining application"
                                : `Delete ${rec.name}`
                            }
                            tooltipPlacement="bottom"
                            disabled={data.length <= 1}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onDeleteClick(rec.id);
                            }}
                          />
                        </Stack>
                      )}
                    </Stack>
                  }
                />
              );
            })}
        </SimpleTreeView>
      </Box>
    </Fragment>
  );
};

export default ApplicationTree;
